// project imports
import { ActionResponseType } from "../../../common/types/common.types";
import { ApiClient } from "../../../core/api/apiClient.service";
import {
  InspectionSetSelectDTO,
  QuestionDTO,
  StandardQuestionBulkUploadDTO,
  UploadFileDTO,
} from "../../../common/types/common.dto.types";
import { AxiosError } from "axios";

// client object for axios calls to backend
const client = new ApiClient();

/* ==============================|| VIEWS - PAGES - BULK UPLOAD QUESTIONS PAGE - API ||==============================  */

/**
 *@name cleanFileBeforeUpload
 * @param uploadedFile - file to be uploaded
 * @description This function prepares the file for upload by clearing unnecessary properties.
 * @returns the cleaned file.
 */
const cleanFileBeforeUpload = (uploadedFile: UploadFileDTO): UploadFileDTO => {
  if (uploadedFile) {
    uploadedFile = {
      ...uploadedFile,
      FilePreview: "",
      FileData: null,
    };
  }

  return uploadedFile;
};

/**
 *
 * @param file - file to be converted to base64
 * @description This function converts a file to base64 format using FileReader API.
 */
const toBase64 = async (file) =>
  await new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => resolve(reader.result);
    reader.readAsDataURL(file);
    reader.onerror = (error) => reject(error);
  });

/**
 *
 * @param base64String - base64 string to be obfuscated
 * @description This function obfuscates a base64 string by shifting each character by 3 positions.
 * @returns the corresponding obfuscated base64 string.
 */
const obfuscateBase64ShiftEncoding = (base64String) => {
  const shift = 3; // Shift each character by 3 positions
  let obfuscatedString = "";

  for (let char of base64String) {
    obfuscatedString += String.fromCharCode(char.charCodeAt(0) + shift);
  }

  return obfuscatedString;
};

export const bulkUploadStandardQuestionApi = async (
  uploadFile: UploadFileDTO
): Promise<ActionResponseType> => {
  try {
    const config = {
      headers: {
        "content-type": "multipart/form-data",
      },
    };
    const URL: string = `StandardQuestion/BulkUploadStandardQuestions`;
    let status: ActionResponseType;

    uploadFile = cleanFileBeforeUpload(uploadFile);

    status = await toBase64(uploadFile.File).then((result) => {
      const base64String: string = result?.toString().split(",")[1];

      // shit data by 3
      const obfuscateBase64ShiftEncodingString =
        obfuscateBase64ShiftEncoding(base64String);
      // encrypt data by urlencoded data
      const urlEncodedString = encodeURIComponent(
        obfuscateBase64ShiftEncodingString
      );
      uploadFile.FileBase64 = urlEncodedString;
      uploadFile.File = null;
      return client
        .post<ActionResponseType>(URL, uploadFile, config)
        .then((response) => {
          return response;
        })
        .catch((error) => {
          status = {} as ActionResponseType;
          return error;
        });
    });
    if (status) return status;
  } catch (error) {
    throw error;
  }
};

export const getBulkUploadQuestionTypeForDropdownApi =
  async (): Promise<ActionResponseType> => {
    try {
      const URL: string = `Question/GetQuestionTypesForDropDown`;
      let result: ActionResponseType = {} as ActionResponseType;

      await client
        .get<ActionResponseType>(URL)
        .then((response) => {
          result = response;
        })
        .catch((error) => {
          return error;
        });
      return result;
    } catch (error) {
      throw error;
    }
  };

export const getBulkUploadQuestionDropdownCategoriesApi = async (
  questionTypeId: number
): Promise<ActionResponseType> => {
  try {
    const URL: string = `Question/GetQuestionDropdownCategories/${questionTypeId}`;
    let result: ActionResponseType = {} as ActionResponseType;

    await client
      .get<ActionResponseType>(URL)
      .then((response) => {
        result = response;
      })
      .catch((error) => {
        return error;
      });
    return result;
  } catch (error) {
    throw error;
  }
};

export const getBulkUploadQuestionCategoryChoicesApi = async (
  question: QuestionDTO
): Promise<ActionResponseType> => {
  try {
    const URL: string = `Question/GetQuestionCategoryChoices`;
    let result: ActionResponseType = {} as ActionResponseType;

    await client
      .post<ActionResponseType>(URL, question)
      .then((response) => {
        result = response;
      })
      .catch((error: AxiosError) => {
        return error;
      });
    return result;
  } catch (error) {
    throw error;
  }
};

export const saveStandardQuestionBulkUploadApi = async (
  standardQuestionBulkUpload: StandardQuestionBulkUploadDTO
): Promise<ActionResponseType> => {
  try {
    const URL: string = `StandardQuestion/SaveStandardQuestionBulkUpload`;
    let result: ActionResponseType = {} as ActionResponseType;

    await client
      .post<ActionResponseType>(URL, standardQuestionBulkUpload)
      .then((response) => {
        result = response;
      })
      .catch((error: AxiosError) => {
        return error;
      });
    return result;
  } catch (error) {
    throw error;
  }
};

export const notifyStandardQuestionIssuesByEmailApi = async (
  standardQuestionBulkUpload: StandardQuestionBulkUploadDTO
): Promise<ActionResponseType> => {
  try {
    const URL: string = `StandardQuestion/SendMailForInvalidQuestionUpload`;
    let result: ActionResponseType = {} as ActionResponseType;

    await client
      .post<ActionResponseType>(URL, standardQuestionBulkUpload)
      .then((response) => {
        result = response;
      })
      .catch((error: AxiosError) => {
        return error;
      });
    return result;
  } catch (error) {
    throw error;
  }
};
